'use client';

import {
  Dialog,
  Stack,
  Typography,
  Box,
  useMediaQuery,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'next/navigation';
import { useQueryState } from 'nuqs';
import { useState, useEffect, useMemo } from 'react';
import { GlassBox } from 'src/components/glass/glass';

import {
  useGetFeedbackFormByEventId,
  useGetFeedbackResponse,
  useSubmitFeedbackForm,
} from '../../../api/hooks/feedback';
import { useSnackbar } from '../../../components/snackbar';
import { FeedbackQuestion, QuestionType, SubmitFeedbackDTO } from '../../../types';
import QuestionForm from './form/question-form';
import HeaderSection from './header-section/header-section';
import MobileActions from './mobile-actions/mobile-actions';
import { formatAnswers } from './utils/feedback.utils';

interface AnswersState {
  [questionId: number]: string | number;
}

export default function FeedbackDialog() {
  const theme = useTheme();
  const { id: eventId } = useParams<{ id: string }>();
  const [open, setOpen] = useQueryState('showFeedback');
  const [slug, setSlug] = useQueryState('slug');
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { data, isLoading, error } = useGetFeedbackResponse(slug);

  const [answers, setAnswers] = useState<AnswersState>({});
  const [submitting, setSubmitting] = useState(false);
  const { data: formData } = useGetFeedbackFormByEventId(Number(eventId), slug);
  const { mutateAsync: submitFeedback } = useSubmitFeedbackForm(Number(eventId));
  const [validationErrors, setValidationErrors] = useState<{ [key: number]: boolean }>({});
  const { enqueueSnackbar } = useSnackbar();
  const questions = useMemo<FeedbackQuestion[]>(
    () => (slug && slug.length > 0 ? data?.form?.questions : formData?.questions) || [],
    [slug, data?.form?.questions, formData?.questions]
  );

  useEffect(() => {
    if (open && questions) {
      const initialAnswers: AnswersState = {};
      questions.forEach((q) => {
        initialAnswers[q.id] = q.questionType === QuestionType.TEXT ? '' : 0;
      });
      setAnswers(initialAnswers);
      setValidationErrors({});
    }
  }, [questions, open]);

  const { isFormValid, missingFieldsMessage } = useMemo(() => {
    if (!questions) {
      return { isFormValid: false, missingFieldsMessage: 'Loading form...' };
    }

    const requiredQuestions = questions.filter((q) => q.isRequired);
    const missingFields: string[] = [];

    requiredQuestions.forEach((q) => {
      const answer = answers[q.id];
      let isInvalid = false;

      if (q.questionType === QuestionType.TEXT) {
        isInvalid = !answer || typeof answer !== 'string' || answer.trim() === '';
      } else {
        isInvalid = typeof answer !== 'number' || answer <= 0;
      }

      if (isInvalid) {
        missingFields.push(q.questionText);
      }
    });

    const isValid = missingFields.length === 0;
    const message = 'Please fill in all required fields';

    return { isFormValid: isValid, missingFieldsMessage: message };
  }, [questions, answers]);

  const handleAnswerChange = (questionId: number, value: string | number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    // Clear validation error when user updates the field
    if (validationErrors[questionId]) {
      setValidationErrors((prev) => ({
        ...prev,
        [questionId]: false,
      }));
    }
  };

  const onClose = () => {
    setOpen(null);
    setAnswers({});
    setSubmitting(false);
    setValidationErrors({});
    setSlug(null);
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setSubmitting(true);

    // Validate required fields
    const requiredQuestions = questions.filter((q) => q.isRequired) || [];
    const newValidationErrors: { [key: number]: boolean } = {};
    let hasErrors = false;

    requiredQuestions.forEach((q) => {
      const answer = answers[q.id];
      let isInvalid = false;

      if (q.questionType === QuestionType.TEXT) {
        isInvalid = !answer || typeof answer !== 'string' || answer.trim() === '';
      } else {
        isInvalid = typeof answer !== 'number' || answer <= 0;
      }

      if (isInvalid) {
        newValidationErrors[q.id] = true;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setValidationErrors(newValidationErrors);
      setSubmitting(false);
      return;
    }

    const userId = Number(localStorage.getItem('userId')) || undefined;
    const formattedAnswers = formatAnswers(answers, questions as FeedbackQuestion[]);
    const submissionDTO: SubmitFeedbackDTO = {
      slug: slug || '',
      formId: formData?.id,
      userId,
      answers: formattedAnswers,
    };

    await submitFeedback(submissionDTO);
    onClose();
    enqueueSnackbar('Feedback submitted successfully!', {
      variant: 'success',
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress sx={{ color: 'rgba(255, 211, 105, 0.8)' }} />
        </Box>
      );
    }

    if (!slug && !formData) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography variant="body1" color="error" align="center">
            Invalid feedback form link. Please use the link in the email you received.
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography variant="body1" color="error" align="center">
            Error loading feedback form. Please try again later.
          </Typography>
        </Box>
      );
    }

    if (data?.answersCount && data?.answersCount > 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography variant="body1" color="error" align="center">
            You have already submitted feedback for this event.
          </Typography>
        </Box>
      );
    }

    return (
      <Stack sx={{ width: '100%' }}>
        <HeaderSection
          fullScreen={mdDown}
          onSubmit={handleSubmit}
          loading={submitting}
          isFormValid={isFormValid}
          missingFieldsMessage={missingFieldsMessage}
        />

        <Box
          sx={{
            p: { xs: 2, md: 4 },
            overflow: 'auto',
            pb: 5,
            maxHeight: { xs: 'calc(100vh - 120px)', md: '75vh' },
          }}
        >
          {data?.form?.title && (
            <Typography
              variant="h4"
              component="h2"
              sx={{
                mb: 3,
                color: 'white',
                textShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                fontWeight: 600,
              }}
            >
              {data?.form?.title}
            </Typography>
          )}

          <Box
            sx={{
              mb: 2,
              p: 3,
              borderRadius: 2,
              backgroundColor: 'rgba(40, 80, 140, 0.4)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(100, 180, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'rgba(100, 180, 255, 0.15)',
                flexShrink: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                i
              </Box>
            </Box>
            <Box>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontWeight: 700,
                  mb: 0.5,
                  fontSize: '1.1rem',
                }}
              >
                Your Secret Thoughts Matter!! 🙊
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                }}
              >
                Tell us what you REALLY think! All responses are 100% <strong>ANONYMOUS</strong> to
                the organizer. No names, no traces—just honest feedback to make our next event even
                better! ✨
              </Typography>
            </Box>
          </Box>
          <Stack spacing={2} sx={{ mt: 1, pb: 10 }}>
            {/* Questions */}
            {questions.map((question, index) => (
              <Paper
                key={question.id}
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: 'rgba(30, 30, 30, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: validationErrors[question.id]
                    ? '1px solid rgba(255, 50, 50, 0.6)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(40, 40, 40, 0.7)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  },
                }}
              >
                <QuestionForm
                  data-qa-id={`feedback-question-${index}`}
                  question={question}
                  value={answers[question.id]}
                  onChange={handleAnswerChange}
                  error={validationErrors[question.id]}
                />

                {validationErrors[question.id] && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'error.main',
                      display: 'block',
                      mt: 1,
                    }}
                  >
                    This field is required
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      </Stack>
    );
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      sx={{
        '& .MuiDialog-paperFullScreen': {
          backgroundColor: 'transparent',
          position: 'relative',
        },
        '& .MuiDialog-paper': {
          width: { md: '800px' },
          maxWidth: { xs: '100%', sm: '90%', md: '800px' },
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          backgroundColor: 'transparent',
          m: { xs: 1, sm: 2, md: 3 },
          overflow: 'hidden',
          height: fullScreen ? '100%' : 'auto',
          maxHeight: fullScreen ? '100%' : '90vh',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      {/* Background blur gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '30vw',
          height: '50vh',
          background: 'linear-gradient(135deg, #000000 0%, #e5f97f 100%)',
          filter: 'blur(150px)',
          opacity: 0.15,
          borderRadius: '50%',
          zIndex: 0,
        }}
      />

      {mdDown && (
        <MobileActions
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          isFormValid={isFormValid}
          missingFieldsMessage={missingFieldsMessage}
        />
      )}

      <GlassBox
        sx={{
          width: '100%',
          height: '100%',
          zIndex: 1,
          position: 'relative',
          borderRadius: fullScreen ? 0 : 2,
          overflow: 'hidden',
          // pb: 2,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(15, 15, 15, 0.6)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {renderContent()}
      </GlassBox>
    </Dialog>
  );
}
